import React from 'react';
import { X, Loader2, CheckCircle2, XCircle, Phone, MapPin, Building2, Truck } from 'lucide-react';
import { deriveTripStage } from './mockTrackingData';

// Shown for "Add & Assign" (an off-platform driver + vehicle registered and
// assigned on the spot) — previously this just closed straight to a toast
// message with no visible confirmation of what was actually created. Registering
// the driver/vehicle and updating the booking is a real backend transaction
// (not instant), so this shows a loading state for that gap, then the same
// rich "booking confirmed" card style used elsewhere in Command Center
// (matching Hospital's booking success screen) once it resolves.
const CommandCenterManualAssignPopup = ({ phase, booking, driver, errorMessage, onClose, onDone }) => {
  const fare = booking?.total_price ?? booking?.estimated_fare ?? booking?.platform_service_fee ?? booking?.agreed_price ?? booking?.system_price;
  const confirmedDriver = booking?.driver;
  const confirmedVehicle = booking?.vehicle;

  // "Done" both closes the popup and takes the dispatcher to the Trips page,
  // opened on whichever tab this booking actually landed in — "Close"/error
  // paths just dismiss.
  const handlePrimaryAction = () => {
    onClose();
    if (phase === 'success') onDone?.(deriveTripStage(booking));
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={phase === 'loading' ? undefined : onClose}>
      <div
        className="bg-white rounded-2xl shadow-xl w-full max-w-md max-h-[90vh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-end p-3 shrink-0">
          <button
            onClick={onClose}
            disabled={phase === 'loading'}
            className="text-gray-400 hover:text-gray-600 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto px-6 pb-6 -mt-2">
          {phase === 'loading' && (
            <div className="text-center py-8">
              <Loader2 className="w-10 h-10 text-primary animate-spin mx-auto mb-4" />
              <h3 className="text-base font-semibold text-gray-900 mb-1">Registering Driver &amp; Vehicle&hellip;</h3>
              <p className="text-sm text-gray-500">Creating the booking assignment. This only takes a moment.</p>
            </div>
          )}

          {phase === 'success' && (
            <div className="text-center">
              <div className="flex justify-center mb-4">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
                  <CheckCircle2 className="w-8 h-8 text-green-600" />
                </div>
              </div>
              <h3 className="text-xl font-semibold text-green-900 mb-1">Booking Confirmed!</h3>
              <p className="text-sm text-green-700 mb-5">
                Driver &amp; vehicle registered and assigned to this booking.
              </p>

              <div className="space-y-3 text-left">
                {booking?.booking_id && (
                  <div className="bg-blue-50 border border-blue-100 rounded-lg p-3 text-center">
                    <p className="text-[10px] uppercase tracking-wider text-blue-500 font-semibold mb-0.5">Booking ID</p>
                    <p className="font-mono tracking-wider text-blue-900 font-semibold">{booking.booking_id}</p>
                  </div>
                )}

                {(booking?.patient_name || fare != null) && (
                  <div className="grid grid-cols-2 gap-3">
                    <div className="bg-gray-50 rounded-lg p-3">
                      <p className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold mb-0.5">Patient</p>
                      <p className="text-sm font-medium text-gray-900 truncate">{booking?.patient_name || '—'}</p>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-3">
                      <p className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold mb-0.5">Est. Fare</p>
                      <p className="text-sm font-medium text-gray-900">{fare != null ? `₹${fare}` : '—'}</p>
                    </div>
                  </div>
                )}

                {(booking?.pickup_address || booking?.dropoff_address) && (
                  <div className="bg-gray-50 rounded-lg p-3">
                    <p className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold mb-2">Trip Route</p>
                    <div className="flex items-start gap-2 mb-1.5">
                      <MapPin className="w-3.5 h-3.5 text-green-600 mt-0.5 flex-shrink-0" />
                      <span className="text-sm text-gray-800">{booking?.pickup_address || 'Pickup location'}</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <Building2 className="w-3.5 h-3.5 text-red-500 mt-0.5 flex-shrink-0" />
                      <span className="text-sm text-gray-800">{booking?.dropoff_address || 'Drop-off location'}</span>
                    </div>
                  </div>
                )}

                <div className="bg-green-50 rounded-lg p-4 space-y-2">
                  <h4 className="font-semibold text-gray-900 text-sm mb-1">Driver Details</h4>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-500">Driver</span>
                    <span className="font-medium text-gray-900">{confirmedDriver?.name || driver?.name}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-500">Vehicle</span>
                    <span className="font-medium text-gray-900 flex items-center gap-1">
                      <Truck className="w-3 h-3" />
                      {confirmedVehicle?.plate_number || driver?.vehiclePlate} &middot; {confirmedVehicle?.type?.name || driver?.vehicleTypeLabel}
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-500">Phone</span>
                    <span className="font-medium text-gray-900 flex items-center gap-1">
                      <Phone className="w-3 h-3" />
                      {confirmedDriver?.phone || driver?.phone}
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-500">Company</span>
                    <span className="font-medium text-gray-900">{confirmedVehicle?.operator?.name || driver?.vendorName}</span>
                  </div>
                  {(driver?.city || driver?.area || driver?.pincode) && (
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-500">Location</span>
                      <span className="font-medium text-gray-900 flex items-center gap-1">
                        <MapPin className="w-3 h-3" />
                        {[driver.area, driver.city, driver.pincode].filter(Boolean).join(', ')}
                      </span>
                    </div>
                  )}
                  <div className="flex items-center justify-center gap-2 p-2.5 bg-green-100 rounded-lg mt-3">
                    <CheckCircle2 className="w-4 h-4 text-green-600" />
                    <span className="text-sm font-semibold text-green-800">Driver Assigned</span>
                  </div>
                </div>

                <p className="text-xs text-gray-400 text-center">
                  This vehicle is flagged unverified until Admin completes its full registration.
                </p>
              </div>
            </div>
          )}

          {phase === 'error' && (
            <div className="text-center">
              <div className="flex justify-center mb-4">
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center">
                  <XCircle className="w-8 h-8 text-red-600" />
                </div>
              </div>
              <h3 className="text-xl font-semibold text-red-900 mb-1">Assignment Failed</h3>
              <p className="text-sm text-red-700">{errorMessage || 'Something went wrong — please try again.'}</p>
            </div>
          )}
        </div>

        {phase !== 'loading' && (
          <div className="border-t border-gray-200 p-4 flex justify-end shrink-0">
            <button
              onClick={handlePrimaryAction}
              className={`py-2 px-4 rounded-lg font-semibold text-sm transition-colors ${
                phase === 'success'
                  ? 'bg-green-600 hover:bg-green-700 text-white'
                  : 'bg-primary hover:bg-primary/90 text-primary-foreground'
              }`}
            >
              {phase === 'success' ? 'Done' : 'Close'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default CommandCenterManualAssignPopup;
